/**
 * `Shell` — the one screen (`decisions/0203`): an always-visible **app strip**, a **rail panel**
 * that shows one page at a time, the **stage** (the GM's table scene), and **COMMS** as a fixed
 * 320px right column.
 *
 *  - **The rail page is the URL** (`useRail`, `railState.ts`): `/` is the closed rail, every other
 *    path is a page. Each page declares a width — narrow pages sit beside the stage, medium pages
 *    cover its left side, wide pages cover it up to COMMS; the stage stays mounted underneath.
 *  - **The stage is server state** (`session.tableScene`), never the URL. A narrative scene shows
 *    the empty GMS desk; an encounter scene mounts COMBAT.app on the stage. Players reach a fight
 *    only by the GM putting it on the table. The GM can also **preview** an encounter privately
 *    (*Peek*, `stageScene.ts`): this client's stage shows it under a hazard-striped
 *    `PEEKING … · ON TABLE: …` bar while every other client keeps the table's scene.
 *  - **One COMMS.** In a narrative scene the column holds the shell's own `CommsApp`; with COMBAT.app
 *    on the stage, COMBAT portals its `CommsApp` (the one wired to the live encounter socket, so
 *    decision cards resolve) into the same column instead (`stageContext.ts`).
 *  - **The strip expands** (icon, name, subtitle per app) when nothing needs the screen — no page
 *    open and a narrative stage — and collapses to icons otherwise (`stripExpanded`). Out of combat
 *    the expanded strip is the open-by-default state; the table moving into an encounter closes an
 *    open page (`railAfterSceneChange`, on scene changes only, so it never fights the viewer).
 *    Collapsed, a hovered or focused icon shows its name and subtitle in a tooltip to its right.
 *  - **Motion:** one highlight block slides to the selected app; a page swap fades the old page
 *    out and slides the new one a few px from the direction of travel; the panel fades in place
 *    when the rail opens, and is simply gone when it closes. Nothing moves sideways: the strip's
 *    width snaps between its states, and a narrow ↔ medium ↔ wide swap changes width instantly.
 *    All of it is under 250ms, never blocks input, and is instant under `prefers-reduced-motion`.
 *  - **The strip footer** carries the GM's Settings (seats and the room lock), LIVE, the clock (in
 *    both strip states), Mute SFX, Switch Pilot (players) and Log Out.
 *  - **FORGE** (`decisions/0204`, `ForgeKey.tsx`): while the guided pilot's build has items left,
 *    its key sits bottom-right of the PILOT and MECH pages and its count is a badge on the Pilot icon
 *    elsewhere. Its state lives here, so the panel stays open across PILOT ↔ MECH.
 *
 * One roster room for the whole screen: `index.tsx` opens it (`useRemoteRoster`) and threads it in,
 * so every page reads and writes the same authoritative state on the same socket.
 */
import { faChessKing, faRightFromBracket, faVolumeHigh, faVolumeXmark } from "@fortawesome/free-solid-svg-icons";
import { AnimatePresence, motion } from "motion/react";
import { Fragment, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { backdropReadout, encounterScene, type CodexEntry, type PilotData, type TableScene } from "@lancer-vtt/domain";
import { listEncounters } from "../../../lib/api";
import { useCompendiumContext } from "../../../lib/CompendiumProvider";
import { useGameSessionContext } from "../../../lib/game/GameSessionProvider";
import type { UseRoster } from "../../../lib/roster/useRemoteRoster";
import { sfxEngine } from "../../../lib/sfx/SfxEngine";
import { Icon } from "../../../lib/Icon";
import { prefersReducedMotion } from "../../../lib/prefersReducedMotion";
import { CombatApp } from "../CombatApp";
import { CommsApp } from "../CommsApp";
import { CommsFilterMenu, CommsFilterProvider } from "../CommsFilterMenu";
import type { Ref } from "../../compendium/compendiumData";
import { ForgeBackChip, ForgeKeyAndPanel, useForgeGuide } from "../ForgeKey";
import { forgeSurface, guidedPilotId } from "../forgeGuide";
import { useClock } from "./useClock";
import { GMS_ASCII } from "../GmsScreen";
import {
  compendiumPageForRef,
  RAIL_APPS_BY_ROLE,
  REFERENCE_APPS,
  RAIL_WIDTH,
  railAfterSceneChange,
  railPageAllowed,
  stripExpanded,
  useRail,
  type RailApp,
  type RailPage,
} from "../railState";
import { RosterAvatar } from "../RosterAvatar";
import { RailPanel } from "./RailPanel";
import { useRailDice } from "./railDice";
import { NarrativeBackdrop } from "./backdrop/NarrativeBackdrop";
import { useBackdropEntry } from "./backdrop/useBackdropEntry";
import { useBackdropFade } from "./backdrop/useBackdropFade";
import { SceneCutOverlay, useSceneCut } from "./SceneCut";
import { sceneKey } from "./sceneCutPlan";
import { StageContext, type StageControls } from "./stageContext";
import { previewAfterTableChange, previewBanner, stageScene } from "./stageScene";
import { STRIP_APPS, stripAppName } from "./stripApps";
import { useRollToastLane } from "./useRollToastLane";
import "../gms-desktop.css";
import "./shell.css";

export function Shell(props: { roster: UseRoster; pilotId: string | null }) {
  // The feed filter outlives a scene change: COMBAT's portalled COMMS and the shell's own read the
  // same selection. Above the body, so a toggle re-renders only the filter's consumers.
  return (
    <CommsFilterProvider>
      <ShellBody {...props} />
    </CommsFilterProvider>
  );
}

function ShellBody({ roster, pilotId }: { roster: UseRoster; pilotId: string | null }) {
  const session = useGameSessionContext();
  const role = session.role === "gm" ? "gm" : "player";
  const rail = useRail();
  // A page outside the viewer's set (a player on `/slots`) is the closed rail for them.
  const page = rail.page !== null && railPageAllowed(rail.page, role) ? rail.page : null;

  // Dice's one opener: the pilot license's trigger rows here, COMBAT's roll buttons through the stage
  // context. A player's lands on the DICE page, the GM's in the COMMS composer.
  const dice = useRailDice(rail.open, role);

  // Each app's last page, so reopening one from the strip lands where the viewer left it.
  const lastPages = useRef<Partial<Record<RailApp, RailPage>>>({});
  useEffect(() => {
    if (page !== null) lastPages.current[page.app] = page;
  }, [page]);

  // Combat clears the screen (`decisions/0203` point 5), on table-scene changes only.
  const scene = session.tableScene;
  const tableKey = sceneKey(scene);
  const prevScene = useRef<TableScene | undefined>(undefined);
  // The GM's private preview (an encounter id): client-local, never in the URL.
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    const next = railAfterSceneChange({ prev: prevScene.current, next: scene, page });
    prevScene.current = scene;
    if (next !== undefined) rail.close({ replace: true });
    setPreview((p) => previewAfterTableChange({ preview: p, table: scene }));
    // Keyed on the scene alone: the rule reads the page as it stands when the scene changes, and
    // a page change must never re-run it (that would fight the viewer's own navigation).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey]);
  // The stage renders `shown`, which trails the scene it should show through the broadcast cut.
  const pinned = useBackdropEntry();
  const backdrop = useBackdropFade(pinned);
  const cut = useSceneCut({
    shown: stageScene({ table: scene, preview: role === "gm" ? preview : null }),
    live: session.status === "authenticated",
    world: pinned !== null ? backdropReadout(pinned).name : null,
  });
  const shown = cut.displayed;
  const shownKey = sceneKey(shown);
  const [commsSlot, setCommsSlot] = useState<HTMLElement | null>(null);
  const stage = useMemo<StageControls>(
    () => ({
      shown,
      preview,
      peek: (encounterId) => setPreview(scene.kind === "encounter" && scene.encounterId === encounterId ? null : encounterId),
      endPreview: () => setPreview(null),
      putOnTable: (next) => {
        if (!session.setTableScene(next)) return false;
        setPreview(null);
        return true;
      },
      commsSlot,
      openDice: dice.openDice,
      commsDice: role === "gm" ? dice.preselect : null,
      sceneReady: cut.markReady,
    }),
    // `shown` follows the scene and preview through the cut; `session.setTableScene` and `cut.markReady`
    // are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableKey, shownKey, preview, commsSlot, session.setTableScene, dice.openDice, dice.preselect, role],
  );

  // FORGE follows the pilot the PILOT or MECH page shows, else the selected pilot.
  const { compendium } = useCompendiumContext();
  const pilots = roster.state?.pilots ?? {};
  const forgePilotId = guidedPilotId({ page, viewerPilotId: pilotId, pilots });
  const forgePilot = forgePilotId !== undefined ? pilots[forgePilotId] : undefined;
  const forge = useForgeGuide(forgePilot, forgePilot ? roster.computed[forgePilot.id] : undefined, compendium);
  const forgeShows = forgeSurface({ app: page?.app, phase: forge.state.phase });
  // A research ↗ trip remembers the page it left, for COMPENDIUM's "◀ BACK TO FORGE".
  const [forgeReturn, setForgeReturn] = useState<RailPage | null>(null);
  const onApp = page?.app;
  useEffect(() => {
    if (onApp !== "compendium") setForgeReturn(null);
  }, [onApp]);
  const research = (ref: Ref) => {
    const from = page;
    void rail.open({ app: "compendium", page: compendiumPageForRef(ref) }).then(() => setForgeReturn(from));
  };

  const defaultPageFor = (app: RailApp): RailPage => {
    switch (app) {
      case "pilot":
        return { app, mode: "view" };
      case "compendium":
        return { app, page: { section: "MECHS", category: "frame", selected: null } };
      case "codex":
        return { app, tab: "rolodex" };
      case "encounters":
        return { app, view: "list" };
      case "mech":
      case "squad":
      case "cheatsheet":
      case "dice":
      case "roster":
      case "settings":
      case "director":
        return { app };
    }
  };
  const onStripPick = (app: RailApp) => {
    if (page?.app === app) {
      rail.close();
      return;
    }
    void rail.open(lastPages.current[app] ?? defaultPageFor(app));
  };

  const apps = RAIL_APPS_BY_ROLE[role];
  const width = page !== null ? RAIL_WIDTH[page.app] : undefined;
  const expanded = stripExpanded({ page, scene: shown });
  // Over a pinned world the strip and COMMS drop their slabs and let the planet run under them.
  const scenic = shown.kind === "narrative" && backdrop !== null;

  // The direction of travel down the strip, for the page swap's slide (+1 down, -1 up). SETTINGS
  // sits in the footer, under every app.
  const prevApp = useRef<RailApp | undefined>(undefined);
  const stripOrder: RailApp[] = [...apps, "settings"];
  const dir = page !== null && prevApp.current !== undefined && stripOrder.indexOf(page.app) < stripOrder.indexOf(prevApp.current) ? -1 : 1;
  useEffect(() => {
    if (page !== null) prevApp.current = page.app;
  }, [page]);
  const motionOn = !prefersReducedMotion();
  // The roll toasts sit on the visible stage, between the open page and COMMS.
  const mainRef = useRef<HTMLDivElement>(null);
  const commsRef = useRef<HTMLElement>(null);
  useRollToastLane(mainRef, commsRef, page === null ? "" : `${page.app}:${width}`);

  return (
    <StageContext.Provider value={stage}>
      <div className={`gms-os shell${role === "gm" ? " shell--gm" : ""}${width ? ` shell--${width}` : ""}${scenic ? " shell--scenic" : ""}${shown.kind === "encounter" ? " shell--map" : ""}`}>
        <nav className={`shell-strip${expanded ? " shell-strip--expanded" : ""}`} aria-label="Apps">
          <StripHeader expanded={expanded} />
          <StripApps
            apps={apps}
            active={page?.app}
            expanded={expanded}
            badges={forgeShows === "badge" ? { pilot: forge.items.length } : {}}
            onPick={onStripPick}
          />
          <div className="shell-strip-gap" />
          <StripFooter
            expanded={expanded}
            pilot={pilotId !== null ? roster.state?.pilots[pilotId] : undefined}
            settingsOpen={page?.app === "settings"}
            onSettings={() => onStripPick("settings")}
          />
        </nav>

        <div className="shell-main" ref={mainRef}>
          <AnimatePresence initial={false}>
            {page !== null && width !== undefined && (
              <motion.section
                key="panel"
                className={`shell-panel shell-panel--${width}`}
                data-app={page.app}
                // A fade in, not a slide: the pane opens in place beside the strip. Closing is
                // instant (the developer's call): the pane is gone as the strip widens.
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, pointerEvents: "auto" }}
                exit={{ opacity: 0, pointerEvents: "none", transition: { duration: 0 } }}
                transition={{ duration: motionOn ? 0.2 : 0, ease: "easeOut" }}
              >
                <AnimatePresence initial={false} custom={{ dir, motionOn }}>
                  <motion.div
                    key={page.app}
                    className="shell-panel-page"
                    custom={{ dir, motionOn }}
                    variants={PAGE_VARIANTS}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: motionOn ? 0.18 : 0, ease: "easeOut" }}
                  >
                    <RailPanel page={page} roster={roster} pilotId={pilotId} dicePreselect={dice.preselect} onOpenDice={dice.openDice} />
                  </motion.div>
                </AnimatePresence>
              </motion.section>
            )}
          </AnimatePresence>
          <Stage roster={roster} backdrop={backdrop} covered={width === "wide"}>
            <SceneCutOverlay cut={cut.cut} card={cut.card} />
          </Stage>
          {forgeShows === "key" && forgePilot && page !== null && (
            <ForgeKeyAndPanel guide={forge} pilot={forgePilot} page={page} viewerPilotId={pilotId} navigate={rail.open} onResearch={research} />
          )}
          {page?.app === "compendium" && forgeReturn !== null && <ForgeBackChip onBack={() => void rail.open(forgeReturn)} />}
        </div>

        <aside className="shell-comms" aria-label="COMMS" ref={commsRef}>
          <div className="shell-comms-head">
            <span>COMMS</span>
            <span className="shell-comms-sub">OMNINET FEED</span>
            <CommsFilterMenu />
          </div>
          <div className="shell-comms-body">
            {/* COMBAT.app portals its own COMMS here while it holds the stage. */}
            <div className="shell-comms-slot" ref={setCommsSlot} />
            {shown.kind === "narrative" && <CommsApp />}
          </div>
        </aside>
      </div>
    </StageContext.Provider>
  );
}

/** A page swap: the old page fades out; the new one slides a few px from the direction of travel. */
type Swap = { dir: number; motionOn: boolean };
const PAGE_VARIANTS = {
  enter: ({ dir }: Swap) => ({ opacity: 0, y: 10 * dir }),
  center: { opacity: 1, y: 0, zIndex: 1, pointerEvents: "auto" as const },
  // Its own (shorter) duration, and none at all under reduced motion: `custom` reaches the exiting
  // page through AnimatePresence, so the outgoing page never outlives a reduced-motion swap.
  exit: ({ motionOn }: Swap) => ({ opacity: 0, zIndex: 0, pointerEvents: "none" as const, transition: { duration: motionOn ? 0.12 : 0 } }),
};

/**
 * The strip's app buttons, with ONE highlight block that slides to the selected app (and fades in
 * place when the selection appears or clears). Collapsed, each button carries a tooltip to its right
 * (name and subtitle) shown at once on hover or focus — pure CSS, so there is no delay.
 */
function StripApps({
  apps,
  active,
  expanded,
  badges,
  onPick,
}: {
  apps: RailApp[];
  active: RailApp | undefined;
  expanded: boolean;
  /** A count on an app's icon: FORGE's items left, on Pilot, off the PILOT and MECH pages. */
  badges: Partial<Record<RailApp, number>>;
  onPick: (app: RailApp) => void;
}) {
  const buttons = useRef(new Map<RailApp, HTMLButtonElement>());
  const [hl, setHl] = useState({ top: 0, height: 0, visible: false, slide: false });
  useLayoutEffect(() => {
    const el = active !== undefined ? buttons.current.get(active) : undefined;
    setHl((prev) =>
      el !== undefined
        ? { top: el.offsetTop, height: el.offsetHeight, visible: true, slide: prev.visible }
        : { ...prev, visible: false, slide: false },
    );
  }, [active, expanded, apps]);

  return (
    <div className="shell-apps">
      <div
        className={`shell-apps-hl${hl.slide ? " shell-apps-hl--slide" : ""}`}
        aria-hidden="true"
        style={{ transform: `translateY(${hl.top}px)`, height: hl.height, opacity: hl.visible ? 1 : 0 }}
      />
      {apps.map((app, i) => {
        const meta = STRIP_APPS[app];
        const badge = badges[app];
        // The REFERENCE group opens under a divider, after the role's own work.
        const opensReference = i > 0 && REFERENCE_APPS.includes(app) && !REFERENCE_APPS.includes(apps[i - 1]!);
        return (
          <Fragment key={app}>
            {opensReference && (
              <div className="shell-apps-divider" role="separator" aria-label="Reference">
                <span className="shell-apps-divider-label" aria-hidden="true">
                  REFERENCE
                </span>
              </div>
            )}
            <button
              ref={(el) => {
                if (el) buttons.current.set(app, el);
                else buttons.current.delete(app);
              }}
              type="button"
              className="shell-app"
              data-app={app}
              aria-label={badge !== undefined ? `${stripAppName(app)} (${badge} FORGE ${badge === 1 ? "item" : "items"} left)` : stripAppName(app)}
              aria-current={active === app ? "page" : undefined}
              onClick={() => onPick(app)}
            >
              <span className="shell-app-icon">
                <Icon icon={meta.icon} />
                {badge !== undefined && <span className="shell-app-badge">{badge}</span>}
              </span>
              <span className="shell-app-text" aria-hidden="true">
                <span className="shell-app-name">{meta.label}</span>
                <span className="shell-app-sub">{meta.subtitle}</span>
              </span>
              <span className="shell-tip" aria-hidden="true">
                <span className="shell-app-name">{meta.label}</span>
                <span className="shell-app-sub">{meta.subtitle}</span>
              </span>
            </button>
          </Fragment>
        );
      })}
    </div>
  );
}

/** The brand lockup: `GMS//OS` over the game's name; collapsed, a compact `G//` mark. */
function StripHeader({ expanded }: { expanded: boolean }) {
  const session = useGameSessionContext();
  const gameName = session.slotList?.gameName;
  return (
    <div className="shell-brand">
      {expanded ? (
        <>
          <div className="shell-brand-mark">
            GMS<b>//</b>OS
          </div>
          {gameName ? <div className="shell-brand-game">{gameName}</div> : null}
        </>
      ) : (
        <div className="shell-brand-mark" aria-label="GMS//OS">
          G<b>//</b>
        </div>
      )}
    </div>
  );
}

/**
 * The session block under the apps, left-aligned on the app icons' column: the GM's Settings row,
 * who you are (a player's row is Switch pilot), the link status and clock, and two icon buttons
 * (sound effects, log out). Collapsed, the same items stack as icons, with the clock under the live dot.
 */
function StripFooter({
  expanded,
  pilot,
  settingsOpen,
  onSettings,
}: {
  expanded: boolean;
  pilot: PilotData | undefined;
  settingsOpen: boolean;
  onSettings: () => void;
}) {
  const session = useGameSessionContext();
  const time = useClock();
  const [sfxMuted, setSfxMuted] = useState(() => sfxEngine.isMuted());
  const toggleSfx = () => {
    if (sfxEngine.isMuted()) sfxEngine.unmute();
    else sfxEngine.mute();
    setSfxMuted(sfxEngine.isMuted());
  };
  const live = session.status === "authenticated" && !session.linkDown;
  const isPlayer = session.role === "player";
  const slotName = session.slots.find((s) => s.id === session.actorId)?.name;
  const title = isPlayer ? pilot?.callsign || "No pilot" : "Game Master";
  const subtitle = isPlayer ? (slotName ?? "Player") : "GM";
  const avatar = (
    <span className="shell-app-icon">
      {isPlayer && pilot ? <RosterAvatar pilot={pilot} /> : (
        <span className="gms-roster-avatar shell-gm-avatar" aria-hidden="true">
          <Icon icon={faChessKing} />
        </span>
      )}
    </span>
  );
  const identityText = (
    <>
      <span className="shell-app-text" aria-hidden="true">
        <span className="shell-app-name">{title}</span>
        <span className="shell-app-sub">{isPlayer ? `${subtitle} · switch pilot` : subtitle}</span>
      </span>
      <span className="shell-tip" aria-hidden="true">
        <span className="shell-app-name">{title}</span>
        <span className="shell-app-sub">{isPlayer ? `${subtitle} · switch pilot` : subtitle}</span>
      </span>
    </>
  );
  const sfxLabel = sfxMuted ? "Sound effects: off" : "Sound effects: on";
  const settings = STRIP_APPS.settings;
  return (
    <div className="shell-strip-foot">
      {!isPlayer && (
        <button
          type="button"
          className="shell-app shell-settings"
          data-app="settings"
          aria-label={stripAppName("settings")}
          aria-current={settingsOpen ? "page" : undefined}
          onClick={onSettings}
        >
          <span className="shell-app-icon">
            <Icon icon={settings.icon} />
          </span>
          <span className="shell-app-text" aria-hidden="true">
            <span className="shell-app-name">{settings.label}</span>
            <span className="shell-app-sub">{settings.subtitle}</span>
          </span>
          <span className="shell-tip" aria-hidden="true">
            <span className="shell-app-name">{settings.label}</span>
            <span className="shell-app-sub">{settings.subtitle}</span>
          </span>
        </button>
      )}
      {isPlayer ? (
        <button type="button" className="shell-app shell-identity" aria-label={`Switch pilot — ${title}`} onClick={() => session.deselectPilot()}>
          {avatar}
          {identityText}
        </button>
      ) : (
        <div className="shell-app shell-identity" role="group" aria-label={`${title} — ${subtitle}`}>
          {avatar}
          {identityText}
        </div>
      )}
      <div className={`shell-status${live ? "" : " shell-status--down"}`}>
        <span className="shell-live" role="status">{live ? "Live" : "Relinking"}</span>
        <span className="shell-clock">{expanded ? `· ${time}` : time}</span>
      </div>
      <div className="shell-actions">
        <button type="button" className="shell-icon-btn" aria-label={sfxLabel} aria-pressed={!sfxMuted} onClick={toggleSfx}>
          <Icon icon={sfxMuted ? faVolumeXmark : faVolumeHigh} />
          <span className="shell-tip shell-tip--always" aria-hidden="true">
            <span className="shell-app-name">{sfxLabel}</span>
          </span>
        </button>
        <button type="button" className="shell-icon-btn" aria-label="Log Out" onClick={() => session.forget()}>
          <Icon icon={faRightFromBracket} />
          <span className="shell-tip shell-tip--always" aria-hidden="true">
            <span className="shell-app-name">Log out</span>
          </span>
        </button>
      </div>
    </div>
  );
}

/**
 * The stage: the scene this client shows (`StageControls.shown`). A narrative scene shows the world
 * the GM pinned as the backdrop (`NarrativeBackdrop`, trailing the pin through `useBackdropFade`), or
 * with none the empty GMS desk (wallpaper mark, tagline, vignette). `covered` (a wide page over the stage) pauses the backdrop's render. An
 * encounter mounts COMBAT.app, keyed on the encounter so switching scenes is a fresh mount. While
 * the GM previews, a hazard-striped bar sits at the stage's top, its only top chrome (COMBAT has no
 * top bar; its round header hangs from the board's top edge, under the bar). `children` is the scene
 * cut's overlay, over all of it.
 */
function Stage({ roster, backdrop, covered, children }: { roster: UseRoster; backdrop: CodexEntry | null; covered: boolean; children: React.ReactNode }) {
  const session = useGameSessionContext();
  const stage = useContext(StageContext)!;
  const { shown, preview } = stage;
  return (
    <main className="shell-stage" aria-label="Stage">
      {shown.kind === "narrative" && backdrop !== null ? (
        <NarrativeBackdrop key={backdrop.id} entry={backdrop} covered={covered} />
      ) : shown.kind === "narrative" ? (
        <div className="shell-stage-desk" role="region" aria-label="Narrative scene">
          <pre className="gms-wallpaper" aria-hidden="true">{GMS_ASCII}</pre>
          <div className="gms-tagline" aria-hidden="true">GENERAL MASSIVE SYSTEMS</div>
          <div className="gms-vignette" />
        </div>
      ) : (
        <div className="shell-stage-combat" role="region" aria-label="Encounter scene" data-encounter={shown.encounterId}>
          {preview !== null && <PreviewBar table={session.tableScene} preview={preview} />}
          <CombatApp key={shown.encounterId} encounterId={shown.encounterId} token={session.token!} roster={roster} />
        </div>
      )}
      {children}
    </main>
  );
}

/** `PEEKING <encounter> · ON TABLE: <scene> · [RETURN] [PUT ON TABLE]` (`decisions/0203` point 7). */
function PreviewBar({ table, preview }: { table: TableScene; preview: string }) {
  const stage = useContext(StageContext)!;
  // Names for the bar, refetched when the preview or the table's scene changes.
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const tableId = table.kind === "encounter" ? table.encounterId : "";
  useEffect(() => {
    let live = true;
    void listEncounters().then((listed) => {
      if (live && listed.ok) setNames(new Map(listed.value.map((e) => [e.id, e.name])));
    });
    return () => {
      live = false;
    };
  }, [preview, tableId]);
  const banner = previewBanner({ table, preview, nameOf: (id) => names.get(id) });
  if (banner === null) return null;
  return (
    <div className="shell-preview" role="status" aria-label="Preview">
      <span>PEEKING {banner.peeking}</span>
      <span aria-hidden="true">·</span>
      <span>ON TABLE: {banner.onTable}</span>
      <span className="shell-preview-fill" />
      <button type="button" className="shell-preview-btn" onClick={stage.endPreview}>
        RETURN
      </button>
      <button type="button" className="shell-preview-btn" onClick={() => stage.putOnTable(encounterScene(preview))}>
        PUT ON TABLE
      </button>
    </div>
  );
}
